import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle } from "lucide-react";
import { SkeletonRow } from "@/components/Skeleton";

/**
 * FAQ accordion. Each question can be expanded independently.
 * Items: [{ id, question, answer, order }]
 */
export default function FAQ({ items = [], loading = false }) {
    const [open, setOpen] = useState(() => new Set());

    if (!items.length && !loading) return null;

    const toggle = (id) => {
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="relative py-24 md:py-32 bg-[color:var(--bg-base)]"
        >
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                    <div className="lg:col-span-7">
                        <div className="label-eyebrow mb-4">08 - FAQ</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            Najczęściej zadawane
                            <br />
                            <span className="text-[color:var(--primary)]">pytania</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-base text-[color:var(--text-secondary)] max-w-xl">
                        Odpowiedzi na najczęściej powtarzające się pytania o
                        Project Ruda Śląska - instalację, kompatybilność, plany
                        rozwoju i wsparcie.
                    </p>
                </div>

                <ul className="space-y-3" data-testid="faq-list">
                    {loading && items.length === 0 ? (
                        Array.from({ length: 3 }).map((_, i) => (
                            <SkeletonRow key={i} />
                        ))
                    ) : (
                        items.map((item, idx) => {
                        const isOpen = open.has(item.id);
                        return (
                            <li
                                key={item.id}
                                data-testid={`faq-item-${item.id}`}
                                className={`panel-yellow overflow-hidden transition-colors ${
                                    isOpen
                                        ? "border-[color:var(--primary)]/40"
                                        : ""
                                }`}
                            >
                                <button
                                    type="button"
                                    data-testid={`faq-toggle-${item.id}`}
                                    onClick={() => toggle(item.id)}
                                    aria-expanded={isOpen}
                                    aria-controls={`faq-answer-${item.id}`}
                                    className="w-full text-left px-5 md:px-6 py-5 flex items-start gap-4 group cursor-pointer"
                                >
                                    <span
                                        className="shrink-0 mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-[color:var(--primary)] min-w-[2.5rem]"
                                        aria-hidden="true"
                                    >
                                        {String(idx + 1).padStart(2, "0")}
                                    </span>
                                    <span className="flex-1 text-white text-base md:text-lg font-medium leading-snug group-hover:text-[color:var(--primary)] transition-colors">
                                        {item.question}
                                    </span>
                                    <span
                                        className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-300 ${
                                            isOpen
                                                ? "border-[color:var(--primary)] bg-[color:var(--primary)] text-[color:var(--bg-base)] rotate-180"
                                                : "border-white/15 text-white/70 group-hover:text-white"
                                        }`}
                                        aria-hidden="true"
                                    >
                                        <ChevronDown size={16} />
                                    </span>
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            id={`faq-answer-${item.id}`}
                                            key="content"
                                            initial={{
                                                height: 0,
                                                opacity: 0,
                                            }}
                                            animate={{
                                                height: "auto",
                                                opacity: 1,
                                            }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{
                                                duration: 0.28,
                                                ease: [0.4, 0, 0.2, 1],
                                            }}
                                            className="overflow-hidden"
                                        >
                                            <div className="px-5 md:px-6 pb-5 -mt-1 pl-[3.75rem] md:pl-[4.25rem]">
                                                <div className="pt-3 border-t border-white/10">
                                                    <p
                                                        data-testid={`faq-answer-text-${item.id}`}
                                                        className="text-sm md:text-[15px] text-[color:var(--text-secondary)] leading-relaxed whitespace-pre-wrap"
                                                    >
                                                        {item.answer ||
                                                            "Brak odpowiedzi."}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </li>
                        );
                    })
                    )}
                </ul>

                <div className="mt-10 flex items-center justify-center gap-2 text-xs text-[color:var(--text-muted)]">
                    <HelpCircle size={14} className="text-[color:var(--primary)]" />
                    <span>
                        Nie znalazłeś odpowiedzi? Napisz do nas - zaktualizujemy FAQ.
                    </span>
                </div>
            </div>
        </section>
    );
}
